import { FC, useRef, useState } from 'react';
import { Props, SCashTransfer } from './types';
import styles from './cashTransferPopup.module.scss';
import InteractiveAlertDialog from 'components/Dialoges/InteractiveAlertDialog';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import TextField from 'components/FormControl/TextField';
import BankDetails from 'components/Common/BankDetailsSearch';
import { useAppContext } from 'contexts/AppContext';
import Label from 'components/Common/Label';
import { formattedRupee } from 'utils/util';

const CashTransferPopup: FC<Props> = (props) => {
  const denominations = Object.keys(props.currencyData || {});
  denominations?.sort((a, b) => +b - +a);

  const [transferState, setTransferState] = useState<SCashTransfer[]>(
    denominations?.map((denomination) => ({
      name: denomination,
      collectedValue: (props.currencyData || {})[denomination],
      transferValue: 0,
    })),
  );
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [transferCash, setTransferCash] = useState(true);
  const denominationTCRefs = useRef(new Map<number, HTMLInputElement | null>());

  const {
    snack,
    dialog: { confirm },
  } = useAppContext();

  const handleTransferValueChange = (index: number, value: number) => {
    const tempState = [...transferState];

    tempState[index].transferValue = value;

    setTransferState(tempState);
  };

  // TCTF - Transfer Count TextField
  const handleTCTFEnter = (key: string, index: number) => {
    if (key === 'Enter') {
      if (index + 1 < transferState.length) {
        denominationTCRefs.current.get(index + 1)?.focus();
      } else {
        denominationTCRefs.current.get(0)?.focus();
      }
    }
  };

  const handleSubmit = async () => {
    if (transferCash && !selectedBankId) {
      snack.show('Please select a Bank or HO', 'error');

      return;
    }

    const allZero: boolean = transferState.reduce(
      (isAllZero, currentDenomination) => isAllZero && currentDenomination.transferValue === 0,
      true,
    );

    if (transferCash && allZero) {
      snack.show(
        'Please set a transfer value for the desired denominations, or select "No" for Cash Transfer and submit.',
        'error',
      );

      return;
    }

    const errorCounts = transferState
      .filter((denomination) => denomination.transferValue > denomination.collectedValue)
      .map((denomination) => denomination.name);

    if (errorCounts.length) {
      await confirm({
        title: `Transfer Value Error`,
        description: `The entered transfer value for denominations ${errorCounts.reduce(
          (denominations, currentDenomination) => denominations + ', ₹' + currentDenomination,
          '',
        )} is greater than the collected value`,
        type: 'warning',
        okText: 'Ok',
        cancelText: 'Go Back',
      });

      return;
    }

    props.onSubmit(transferCash, transferState, selectedBankId as string);
  };

  return (
    <InteractiveAlertDialog
      open={props.open}
      title='Confirm Cash Transfer'
      content={
        <Box className={styles.content}>
          <Stack
            direction='row'
            px={1}
            mb={1}
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <Stack direction='row' flex={2} alignItems='center' gap={1}>
              <Typography variant='body2'>
                Would you like to transfer cash to Bank or HO?
              </Typography>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'
                  value={transferCash ? 'yes' : 'no'}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setTransferCash((event.target as HTMLInputElement).value === 'yes')
                  }
                >
                  <FormControlLabel value={'yes'} control={<Radio />} label='Yes' />
                  <FormControlLabel value={'no'} control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
            </Stack>
            {transferCash && (
              <Box className={styles.bankSelectionContainer}>
                <BankDetails
                  bankId={selectedBankId ?? ''}
                  onChange={(value) => setSelectedBankId(value?.id ?? '')}
                  error={!selectedBankId}
                  required
                />
              </Box>
            )}
          </Stack>
          <div className={styles.productsCard}>
            <Table sx={{ minWidth: 640 }} aria-label='simple table' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>Denomination</TableCell>
                  <TableCell align='center'>Collected Count</TableCell>
                  {transferCash && <TableCell align='center'>Transfer Count</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {transferState.map((denomination, index) => (
                  <TableRow
                    key={`$row-${index}-${denomination.name}`}
                    id={`$row-${index}-${denomination.name}`}
                    className={`${styles.productRow}`}
                    onClick={() => denominationTCRefs.current.get(index)?.focus()}
                  >
                    <TableCell align='center' key={index}>
                      ₹{denomination.name}
                    </TableCell>
                    <TableCell align='center' key={index}>
                      {denomination.collectedValue}
                    </TableCell>
                    {transferCash && (
                      <TableCell align='center' key={index}>
                        <TextField
                          inputRef={(el) => {
                            if (el) denominationTCRefs.current.set(index, el);
                          }}
                          size='small'
                          type='integer'
                          value={denomination.transferValue}
                          onKeyDown={(e) => handleTCTFEnter(e.key, index)}
                          onChange={(event) =>
                            !isNaN(+event.target.value) &&
                            handleTransferValueChange(index, +event.target.value)
                          }
                          variant='standard'
                          required
                          error={isNaN(denomination.transferValue)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Label
              title='Collected Value'
              subtitle={formattedRupee(
                transferState.reduce(
                  (reportedValue, currentDenomination) =>
                    reportedValue + currentDenomination.collectedValue * +currentDenomination.name,
                  0,
                ),
              )}
            />
            <Divider orientation='vertical' sx={{ borderRightWidth: '2px' }} flexItem />
            <Label
              title='Transfer Value'
              subtitle={formattedRupee(
                transferState.reduce(
                  (reportedValue, currentDenomination) =>
                    reportedValue + currentDenomination.transferValue * +currentDenomination.name,
                  0,
                ),
              )}
            />
          </Stack>
        </Box>
      }
      actions={[
        {
          label: 'Cancel',
          handler: props.onClose,
          color: 'secondary',
        },
        {
          label: 'Submit',
          handler: handleSubmit,
        },
      ]}
      onClose={props.onClose}
    />
  );
};
export default CashTransferPopup;
